import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";

import { Avatar } from "../../types/avatars.d";

interface AvatarIndexProps {
  avatars: Avatar[];
}

// #STYLE_GUIDE: Index component shows list with delete functionality
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
export default function AvatarIndex({ avatars }: AvatarIndexProps) {
  const { loading, makeRequest } = useApiRequest({
    onSuccess: () => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit("/avatars");
    },
    onError: (error) => {
      console.error("Failed to delete avatar:", error);
      // #TODO: Add proper error handling/toast notifications
    },
  });

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this avatar?")) {
      await makeRequest("DELETE", `/api/v1/avatars/${id}`);
    }
  };

  const handleView = (id: string) => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/avatars/${id}`);
  };

  const handleEdit = (id: string) => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/avatars/${id}/edit`);
  };

  const handleCreate = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit("/avatars/new");
  };

  return (
    <div className="space-y-6">
      {/* Header with Create button */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Avatars</h1>
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          Create New Avatar
        </button>
      </div>

      {/* Table using daisyUI */}
      <div className="overflow-x-auto">
        <table className="table table-zebra">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Created at</th>
              <th>Updated at</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {avatars.map((avatar) => (
              <tr key={avatar.id}>
                <td>{avatar.id}</td>
                <td>{avatar.name}</td>
                <td>{avatar.createdAt}</td>
                <td>{avatar.updatedAt}</td>
                <td>{new Date(avatar.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="join">
                    <button
                      onClick={() => handleView(avatar.id)}
                      className="btn btn-sm btn-outline join-item"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(avatar.id)}
                      className="btn btn-sm btn-outline join-item"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(avatar.id)}
                      className="btn btn-sm btn-error join-item"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {avatars.length === 0 && (
        <div className="text-center py-8">
          <p className="text-base-content/60">No avatars found.</p>
          <button
            onClick={handleCreate}
            className="btn btn-primary mt-4"
          >
            Create Your First Avatar
          </button>
        </div>
      )}
    </div>
  );
}
